import { createReducer, on, Action } from '@ngrx/store';

import * as AccountActions from './account.actions';
import { IAccountState } from './account.models';

export const ACCOUNT_FEATURE_KEY = 'account';

export interface AccountPartialState {
  readonly [ACCOUNT_FEATURE_KEY]: IAccountState;
}

const stateTypeGuard = <T>(state: T): T => state;

const reducer = createReducer(
  {} as IAccountState,
  on(
    AccountActions.initAccountState,
    AccountActions.clearAccountState,
    () => stateTypeGuard<IAccountState>({
      id: 'account-state',
      name: 'account',
      account: {
        isSuccess: false,
        isProcessing: false,
        details: [],
        // Other properties of debitCredit if any
      },
    })
  ),
  on(AccountActions.loadAccountStateSuccess, (state) => stateTypeGuard<IAccountState>({
    ...state,
    isLoaded: true,
  })),
  on(AccountActions.loadAccountStateFailure, (state, { error }) => stateTypeGuard<IAccountState>({
    ...state,
    error
  })),


  /**
   * GET API Request
   */
  on(AccountActions.getAccountBalanceAPI, (state) => stateTypeGuard<IAccountState>({
    ...state,
    balance: {
      ...state.balance,
      response: undefined
    },
    error: undefined
  })),

  on(AccountActions.getUserAccountAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    account: {
      ...(state as IAccountState).account,
      isSuccess: undefined,
      isProcessing: true,
      details: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AccountActions.getAccountStatementsAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    statements: {
      ...(state as IAccountState).statements,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AccountActions.selfExcludeOrSuspendAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    selfExcludeOrSuspend: {
      ...(state as IAccountState).selfExcludeOrSuspend,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AccountActions.selfExcludeOrSuspendVerificationAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    selfExcludeOrSuspendVerification: {
      ...(state as IAccountState).selfExcludeOrSuspendVerification,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AccountActions.getAccountPreferencesAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    configuration: {
      ...(state as IAccountState).configuration,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  /**
   * UPDATE API Request
   */
  on(AccountActions.upsertAccountPrefenceFieldAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    updateAccountPreferences: {
      ...(state as IAccountState).updateAccountPreferences,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  on(AccountActions.getAccountDailyLossLimitAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    lossLimit: {
      ...(state as IAccountState).lossLimit,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),

  /**
   * UPDATE API Request
   */
  on(AccountActions.updateAccountDailyLossLimitAPI, (state) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    updateLossLimit: {
      ...(state as IAccountState).updateLossLimit,
      isProcessing: true,
      isSuccess: undefined,
      response: undefined
    },
    error: undefined
  })),


  /**
   * GET API Response SUCCESS
   */
  on(AccountActions.getAccountBalanceAPISuccess, (state, { successResponse }) => stateTypeGuard<IAccountState>({
    ...state,
    balance: {
      ...state.balance,
      response: successResponse,
      CurrentBalance: successResponse.CurrentBalance
    },
  })),

  on(AccountActions.getUserAccountAPISuccess, (state, { successResponse }) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    account: {
      ...(state as IAccountState).account,
      details: successResponse
    },
  })),

  on(AccountActions.getAccountStatementsAPISuccess, (state, { successResponse }) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    statements: {
      ...(state as IAccountState).statements,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(AccountActions.selfExcludeOrSuspendAPISuccess, (state, { successResponse }) =>
    stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    selfExcludeOrSuspend: {
      ...(state as IAccountState).selfExcludeOrSuspend,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(AccountActions.selfExcludeOrSuspendVerificationAPISuccess, (state, { successResponse }) =>
    stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    selfExcludeOrSuspendVerification: {
      ...(state as IAccountState).selfExcludeOrSuspendVerification,
      isProcessing: false,
      isSuccess: true,
      response: successResponse,
    }
  })),

  on(AccountActions.getAccountPreferencesAPISuccess, (state, { successResponse }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      configuration: {
        ...(state as IAccountState).configuration,
        isProcessing: false,
        isSuccess: true,
        response: successResponse,
      }
  })),

  // updateAccountPreferences
  on(AccountActions.upsertAccountPrefenceFieldAPISuccess, (state, { fieldName, value }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      configuration: {
        ...(state as IAccountState).configuration,
        isProcessing: false,
        isSuccess: true,
        response: {
          ...state?.configuration?.response,
          AccountPreferences: {
            ...state?.configuration?.response?.AccountPreferences,
            [fieldName]:
            fieldName === 'SortBy' ?
            value : fieldName === 'EventTypeFilter' ?
            value : value === 'true' ?
            true : false
          }
        },
      }
  })),

  // wagering favorite tracks
  on(AccountActions.upsertAccountPrefenceFieldFavoriteAPISuccess,
    (state, { fieldName, value }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      configuration: {
        ...(state as IAccountState).configuration,
        isProcessing: false,
        isSuccess: true,
        response: {
          ...state?.configuration?.response,
          AccountPreferences: {
            ...state?.configuration?.response?.AccountPreferences,
            [fieldName]: value
          }
        },
      }
    })
  ),

  // amwager theme
  on(AccountActions.upsertAccountPrefenceFieldAmwagerThemeAPISuccess,
    (state, { fieldName, value }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      configuration: {
        ...(state as IAccountState).configuration,
        isProcessing: false,
        isSuccess: true,
        response: {
          ...state?.configuration?.response,
          AccountPreferences: {
            ...state?.configuration?.response?.AccountPreferences,
            [fieldName]: Number(value)
          }
        },
      }
    })
  ),

  // wagering limits
  on(AccountActions.upsertAccountPrefenceFieldWageringLimitsAPISuccess, (state, { fieldName, value }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      configuration: {
        ...(state as IAccountState).configuration,
        isProcessing: false,
        isSuccess: true,
        response: {
          ...state?.configuration?.response,
          AccountPreferences: {
            ...state?.configuration?.response?.AccountPreferences,
            [fieldName]:
              fieldName === 'WagerLimit' ?
              Number(value) : value === 'true' ?
              true : false
          }
        },
      }
  })),

  on(AccountActions.getAccountDailyLossLimitAPISuccess, (state, { successResponse }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      lossLimit: {
        ...(state as IAccountState).lossLimit,
        isProcessing: false,
        isSuccess: true,
        response: successResponse,
      }
  })),

  // updateAccountPreferences
  on(AccountActions.updatetAccountDailyLossLimitAPISuccess, (state, { amount }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      lossLimit: {
        ...(state as IAccountState).lossLimit,
        isProcessing: false,
        isSuccess: true,
        response: {
          ...state?.lossLimit?.response,
          ['dailyLossLimit']: amount
        },
      }
  })),

  /**
   * GET API Response ERROR
   */
  on(AccountActions.getAccountBalanceAPIFailed, (state, { errorResponse }) => stateTypeGuard<IAccountState>({
    ...state,
    balance: {
      ...state.balance,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AccountActions.getUserAccountAPIFailed, (state, { errorResponse }) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    account : {
      ...(state as IAccountState).account,
      isSuccess: false,
      isProcessing: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AccountActions.getAccountStatementsAPIFailed, (state, { errorResponse }) => stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    statements: {
      ...(state as IAccountState).statements,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AccountActions.selfExcludeOrSuspendAPIFailed, (state, { errorResponse }) =>
    stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    selfExcludeOrSuspend: {
      ...(state as IAccountState).selfExcludeOrSuspend,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AccountActions.selfExcludeOrSuspendVerificationAPIFailed, (state, { errorResponse }) =>
    stateTypeGuard<IAccountState>({
    ...state as IAccountState,
    selfExcludeOrSuspendVerification: {
      ...(state as IAccountState).selfExcludeOrSuspendVerification,
      isProcessing: false,
      isSuccess: false,
      response: errorResponse
    },
    error: errorResponse
  })),

  on(AccountActions.getAccountPreferencesAPIFailed, (state, { errorResponse }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      configuration: {
        ...(state as IAccountState).configuration,
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
      error: errorResponse
  })),

  on(AccountActions.upsertAccountPrefenceFieldAPIFailed, (state, { errorResponse }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      updateAccountPreferences: {
        ...(state as IAccountState).updateAccountPreferences,
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
      error: errorResponse
  })),

  on(AccountActions.getAccountDailyLossLimitAPIFailed, (state, { errorResponse }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      lossLimit: {
        ...(state as IAccountState).lossLimit,
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
      error: errorResponse
  })),

  on(AccountActions.updateAccountDailyLossLimitAPIFailed, (state, { errorResponse }) =>
    stateTypeGuard<IAccountState>({
      ...state as IAccountState,
      lossLimit: {
        ...(state as IAccountState).lossLimit,
        isProcessing: false,
        isSuccess: false,
        response: errorResponse
      },
      error: errorResponse
  })),



  /**
   * Properties
   */
  on(AccountActions.setError, (state, { error }) => stateTypeGuard<IAccountState>({
    ...state,
    error
  })),

  on(AccountActions.setBalance, (state, { balance }) => stateTypeGuard<IAccountState>({
    ...state,
    balance: {
      ...state.balance,
      response: undefined,
      CurrentBalance: balance
    },
  })),

);

export function accountReducer(
  state: IAccountState | undefined,
  action: Action
) {
  return reducer(state, action);
}
